// @mui
import { Box, Card, Grid, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// components
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { organizationProfessionalConfigApi } from 'src/api/organization-details';
import { visitCategoriesApi } from 'src/api/visit-categories';
import { useUserContext } from 'src/auth/useUserContext';
import Editor from 'src/components/editor';
import FormProvider, { RHFMultiSelect } from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import SpecializationRemovalWarningModal from 'src/components/specialization/SpecializationRemovalWarningModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { mapCategoryNameToVisitCategory } from 'src/utils/mapCategoryNameToVisitCategory';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { GetProfessionalConfigDto, GetSpecializationsDto } from 'woofwoof-api';
import OrganizationDetailsInfoCard from '../OrganizationDetailsInfoCard';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

export type OrganizationDetailsProfessionalFormValuesProps = {
  description: string;
  specializations: string[];
};

export type OrganizationDetailsProfessionalFormProps = {
  fetchOrganizationDetails: () => void;
  organizationDetails?: GetProfessionalConfigDto;
  specializations: GetSpecializationsDto[];
  professionalId?: string;
};

export default function OrganizationDetailsProfessionalForm({
  fetchOrganizationDetails,
  organizationDetails,
  specializations,
  professionalId: professionalIdProp,
}: OrganizationDetailsProfessionalFormProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { organizationId: organizationIdParam } = useParams();
  const { data: user } = useUserContext();
  const { selectedOrganization } = useOrganizationContext();

  // Use URL param if available, otherwise use selected organization from context
  const organizationId = organizationIdParam || selectedOrganization?.id;

  // Use prop if provided (admin viewing professional), otherwise use current user id
  const professionalId = professionalIdProp || user?.id;

  // Warning modal state
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [affectedServices, setAffectedServices] = useState<
    Array<{
      serviceId: string;
      serviceName: string;
      specializationName: string;
    }>
  >([]);
  const [pendingData, setPendingData] =
    useState<OrganizationDetailsProfessionalFormValuesProps | null>(null);

  const { mutateAsync: updateProfessionalConfig, isLoading: isUpdateLoading } =
    useMutation({
      mutationFn: (data: {
        description?: string;
        specializations?: string[];
      }) =>
        organizationProfessionalConfigApi.organizationProfessionalConfigControllerSetConfig(
          {
            organizationId: organizationId!,
            professionalId: professionalId!,
            setProfessionalConfigDto: {
              enabledVisitTypes: organizationDetails?.enabledVisitTypes || [],
              enabledCategories: organizationDetails?.enabledCategories || [],
              visitTypeCategoryConfigs:
                organizationDetails?.visitTypeCategoryConfigs?.map(
                  (config) => ({
                    visitType: config.visitType!,
                    category: config.category!,
                  }),
                ) || [],
              serviceConfigs: (organizationDetails?.serviceConfigs ||
                []) as any,
              description: data.description,
              specializations: data.specializations,
            },
          },
        ),
    });

  const specializationsData = specializations || [];

  const { data: categoriesData } = useQuery(['visitCategories'], () =>
    visitCategoriesApi.visitCategoriesControllerGetAllVisitCategories(),
  );

  const categories = categoriesData?.data || [];
  const categoryIdToNameMap = categories.reduce(
    (acc: Record<string, string>, cat: any) => {
      acc[cat.id] = mapCategoryNameToVisitCategory(cat.name);
      return acc;
    },
    {},
  );

  const specializationsOptions = specializationsData?.map(
    (specialization: any) => ({
      value: specialization.id,
      label: mapSpecializationNameIdToString(specialization),
      group: categoryIdToNameMap[specialization.visitCategoryId] || translateKey("search.specializationOther"),
    }),
  );

  const UpdateOrganizationSchema: Yup.Schema<OrganizationDetailsProfessionalFormValuesProps> =
    Yup.object().shape({
      description: Yup.string().required('Opis jest wymagany'),
      specializations: Yup.array()
        .min(1, 'Co najmniej jedna specjalizacja jest wymagana')
        .required(translateKey("hardcoded.specjalizacje_sa_wymagane")),
    });

  const defaultValues: OrganizationDetailsProfessionalFormValuesProps = {
    description: organizationDetails?.description || '',
    specializations: organizationDetails?.specializations || [],
  };

  const methods = useForm<OrganizationDetailsProfessionalFormValuesProps>({
    resolver: yupResolver(UpdateOrganizationSchema),
    defaultValues,
    values: defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    getValues,
    formState: { errors },
  } = methods;

  const formValues = getValues();

  const handleConfirmRemoval = async () => {
    if (!pendingData) return;

    setShowWarningModal(false);

    try {
      await updateProfessionalConfig({
        description: pendingData.description,
        specializations: pendingData.specializations,
      });

      enqueueSnackbar(translateKey("hardcoded.ustawienia_w_placowce_zostaly_zaktualizowane"), {
        variant: 'success',
      });
      fetchOrganizationDetails();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    } finally {
      setPendingData(null);
      setAffectedServices([]);
    }
  };

  const handleCancelRemoval = () => {
    setShowWarningModal(false);
    setPendingData(null);
    setAffectedServices([]);
  };

  const handleOnSubmit = async (
    data: OrganizationDetailsProfessionalFormValuesProps,
  ) => {
    // Check if any specializations are being removed
    const currentSpecs = organizationDetails?.specializations || [];
    const newSpecs = data.specializations;
    const removedSpecs = currentSpecs.filter((id) => !newSpecs.includes(id));

    if (removedSpecs.length > 0) {
      // Check if removed specializations have assigned services
      try {
        const response =
          await organizationProfessionalConfigApi.organizationProfessionalConfigControllerCheckSpecializationServices(
            {
              organizationId: organizationId!,
              professionalId: professionalId!,
              checkSpecializationServicesDto: {
                specializationIds: removedSpecs,
              },
            },
          );

        if (response.data?.hasAssignedServices) {
          // Show warning modal
          setAffectedServices(response.data.affectedServices);
          setPendingData(data);
          setShowWarningModal(true);
          return;
        }
      } catch (error) {
        console.error('Failed to check specialization services:', error);
        // Continue with save if check fails
      }
    }

    // No removed specializations or no affected services, proceed directly
    try {
      await updateProfessionalConfig({
        description: data.description,
        specializations: data.specializations,
      });

      enqueueSnackbar(translateKey("hardcoded.ustawienia_w_placowce_zostaly_zaktualizowane"), {
        variant: 'success',
      });
      fetchOrganizationDetails();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(handleOnSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={4} xl={4}>
          <OrganizationDetailsInfoCard isLoading={isUpdateLoading} />
        </Grid>

        <Grid item xs={12} md={12} lg={8} xl={8}>
          <Card
            sx={{
              p: { xs: 2, sm: 2, md: 3, lg: 3, xl: 3 },
              display: 'flex',
              flex: 1,
              boxShadow: theme.customShadows.z24,
            }}
          >
            <Stack sx={{ height: 1, flex: 1 }}>
              <Stack
                sx={{
                  pb: 3,
                  gap: 3,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: 3, md: 2 },
                  }}
                >
                  <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                    <RHFMultiSelect
                      chip
                      required
                      name="specializations"
                      label="Specjalizacje"
                      disabled={isUpdateLoading}
                      options={specializationsOptions}
                      sx={{ width: '100%' }}
                    />
                  </Box>

                  <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                    <Editor
                      sx={{ minHeight: '500px' }}
                      disabled={isUpdateLoading}
                      placeholder={translateKey("hardcoded.twoj_opis")}
                      simple
                      id="simple-editor"
                      value={formValues.description}
                      onChange={(_value) => {
                        if (_value.replace(/<(.|\n)*?>/g, '').trim()) {
                          setValue('description', _value);
                        } else {
                          setValue('description', '');
                        }
                      }}
                      helperText={errors.description?.message}
                    />
                  </Box>
                </Box>
              </Stack>

              <Stack
                sx={{
                  flex: 1,
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}
              >
                <Stack>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isUpdateLoading}
                  >{translateKey("common.save")}</LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <SpecializationRemovalWarningModal
        open={showWarningModal}
        onClose={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
        affectedServices={affectedServices}
        isProfessional={true}
      />
    </FormProvider>
  );
}
